import React, { useEffect, useState } from "react";
import {
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ShowHideColumnsButton,
} from "material-react-table";
import { Box, styled, ThemeProvider, Typography } from "@mui/material";

export const TableWrapper = styled("div")(({
  "& .chipContainer": {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    maxWidth: "100%", // Ensure chips don't overflow the container
    overflow: "hidden",
  },
  "& .MuiChip-root": {
    height: "22px",
  },
  "& .subChipText": {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "18px",
  },
  "& .MuiBox-root": {
    boxShadow: "none",
  },
  "& .MuiIconButton-root": {
    padding: "0px",
    fontSize: "1.25rem",
    color: "#999999",
  },
  "& .MuiTableRow-root": {
    boxShadow: "none",
  },
  "& .MuiTableRow-root:hover td:after": {
    backgroundColor: "#FFFFFF !important",
  },
  "& .MuiInputBase-input": {
    fontSize: "13px",
    fontWeight: 400,
    padding: '4px 10px !important',
  },
  "& .MuiCollapse-root": {
    width: "100%",
  },
  "& .Mui-TableBodyCell-DetailPanel": {
    padding: "1px",
  },
}));
// Define types for the props
interface CustomTableProps {
  columns: any; // Replace 'any' with your specific column type
  data?: any[]; // Replace 'any' with your specific data type
  hiddenColumns?: string[];
  title?: string;
  filteredData?: (table: any) => void; // Replace 'any' with your specific table type
  handlePaginationChange?: (pagination: any) => void; // Replace 'any' with your specific pagination type
  inWhiteScreen?: boolean;
  creatingRow?: boolean;
  onClickRow?: (row: any) => void; // Replace 'any' with your specific row typ
  globalSearchValue?: string;
  rowCount?: number; // Optional for backward compatibility
  renderBottomToolbar?: React.ReactNode;
  onCreatingRowCancel?: () => void;
  onCreatingRowSave?: () => void;
  onEditingRowSave?: () => void;
  onEditingRowCancel?: () => void;
  setPaginations?: (pagination: any) => void; // Replace 'any' with your specific pagination type
  paginations?: { pageIndex: number; pageSize: number };
  enableTopToolbar?: boolean;
  enableBottomToolbar?: boolean;
  columnFilter?: any; // Replace 'any' with your specific column filter type
  darkBackground?: boolean;
  enableRowActions?: boolean;
  enableRowSelection?: boolean;
  enableColumnFilters?: boolean;
  enableEditing?: boolean;
  renderRowActions?: (row: any) => React.ReactNode; // Replace 'any' with your specific row type
  enableSorting?: boolean;
  columnVisibility?: any; // Replace 'any' with your specific column visibility type
  handleSearchBar?: boolean;
  handleCustomFilter?: boolean;
  enableExpanding?: boolean;
  searchBarFiterProps?: any; // Replace 'any' with your specific search bar filter props type
  searchBarOnChange?: (value: string) => void; // Replace 'string' with your specific search value type
  enablePagination?: boolean;
  isRefetchingTodos?: boolean;
  isLoadingTodos?: boolean;
  renderTopToolBar?: (table: any) => React.ReactNode;
  renderDetailPanel?: ({ row }: { row: any }) => React.ReactNode;
  onFullScreen?: (table: any) => void; // Replace 'any' with your specific table type
}

const getColumnOrder = (columns: any[]): string[] =>
  columns
    .map((column) => column.id ?? column.accessorKey)
    .filter((columnId): columnId is string => Boolean(columnId));

const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  data = [],
  hiddenColumns = [],
  title,
  filteredData,
  setPaginations,
  paginations = { pageIndex: 0, pageSize: 10 },
  enableTopToolbar = true,
  enableBottomToolbar = true,
  columnFilter,
  darkBackground = false,
  enableRowActions = true,
  rowCount, // Optional - only used if provided
  enableRowSelection = false,
  enableColumnFilters = true,
  enableEditing = false,
  enableExpanding,
  renderRowActions = ({ row }) => <></>,
  enableSorting = true,
  onClickRow = () => { },
  handleSearchBar = true,
  searchBarFiterProps = {},
  searchBarOnChange,
  renderTopToolBar = ({ table }) => {
    return (

      <Box
        sx={{ display: "flex", justifyContent: 'flex-end', gap: "0.5rem", alignItems: "center", padding: "0.5rem", backgroundColor: '#FFFFFF' }}
      >
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
      </Box>
    );
  },
  renderDetailPanel,
  enablePagination = true,
  isRefetchingTodos = false,
  isLoadingTodos = false,
  onFullScreen,
}) => {
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => getColumnOrder(columns));
  const [columnVisibility, setColumnVisibility] = useState(() => {
    return hiddenColumns.reduce((acc, column) => {
      acc[column] = false;
      return acc;
    }, {} as Record<string, boolean>);
  });

  useEffect(() => {
    if (columnFilter) {
      setColumnFilters(columnFilter);
    }
  }, [columnFilter]);

  useEffect(() => {
    const nextColumnOrder = getColumnOrder(columns);
    setColumnOrder((prevColumnOrder) => {
      const isSameOrder =
        prevColumnOrder.length === nextColumnOrder.length &&
        prevColumnOrder.every((columnId, index) => columnId === nextColumnOrder[index]);
      return isSameOrder ? prevColumnOrder : nextColumnOrder;
    });
  }, [columns]);

  const table = useMaterialReactTable({
    columns,
    data,
    columnResizeMode: "onEnd",
    enableColumnActions: false,
    enableSorting,
    enableColumnFilters,
    enableRowActions: enableRowActions,
    renderRowActions: renderRowActions,
    enableTopToolbar,
    enableBottomToolbar,
    enableExpanding,
    localization: {
      //   noRecordsToDisplay: <div>No Records to Display</div>,
      rowsPerPage: '',
      hideAll: "Hide All",
      showAll: "Show All",
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
      },
      "mrt-row-numbers": {
        enableResizing: true,
        size: 100,
        grow: true,
      },
    },
    ...(rowCount !== undefined && { rowCount }), // Only include if provided
    enableRowNumbers: false,
    layoutMode: "grid",
    onColumnFiltersChange: setColumnFilters,
    enableFacetedValues: true,
    enableFilterMatchHighlighting: false,
    state: {
      pagination: paginations,
      columnFilters,
      columnOrder,
      density: "compact",
      columnVisibility,
      //   globalFilter: "", // Assuming you want to pass this as a prop
      isLoading: isLoadingTodos,
      showProgressBars: isRefetchingTodos,
    },
    initialState: {
      columnSizing: { maxSize: 500 },
      columnVisibility,
      columnPinning: {
        left: ['mrt-row-expand'],
        right: ['mrt-row-actions'],
      },
    },
    onColumnOrderChange: setColumnOrder,
    onPaginationChange: setPaginations,
    muiTopToolbarProps: {
      sx: {
        bgcolor: darkBackground ? '#333' : '#fff', // Direct colors
      },
    },
    renderDetailPanel: renderDetailPanel,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        color: '#ffffff', // Direct colors
        backgroundColor: '#2C4748',
        '& .MuiTableSortLabel-root': {
          color: '#fff', // text color of the label
          '&:hover': {
            color: '#fff',
          },
          '&.Mui-active': {
            color: '#fff',
          },
        },
        '& .MuiTableSortLabel-icon': {
          color: '#fff !important', // force the icon to white
        },
      },
    },
    muiTableHeadRowProps: {
      sx: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        bgcolor: darkBackground ? '#333' : '#fff', // Direct colors
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      hover: false,
      sx: {
        fontWeight: 400,
        bgcolor: darkBackground ? '#444' : '#fff', // Direct colors
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
      },
      // onClick: onClickRow,
      onClick: () => {
        onClickRow(row.original);
      }
    }),
    muiTableBodyCellProps: {
      sx: { padding: "8px", fontWeight: 400, textWrap: 'wrap' },
    },
    muiBottomToolbarProps: {
      sx: {
        bgcolor: darkBackground ? '#333' : '#fff', // Direct colors
      },
    },
    positionActionsColumn: "last",
    renderTopToolbar: renderTopToolBar,
    paginationDisplayMode: "pages",
    manualPagination: true,
    muiTablePaperProps: ({ table }) => ({
      style: {
        margin: table.getState().isFullScreen ? "0 0 0 16%" : undefined,
        width: table.getState().isFullScreen ? "100%" : undefined,
        maxWidth: table.getState().isFullScreen ? "84%" : undefined,
        padding: table.getState().isFullScreen ? "2rem 2rem" : undefined,
        backgroundColor: table.getState().isFullScreen ? '#f4f4f4' : undefined, // Direct colors
      },
    }),
    muiPaginationProps: {
      color: "primary",
      className: "page",
      showRowsPerPage: false,
      showFirstButton: false,
      showLastButton: false,
      variant: "outlined",
      sx: {
        boxShadow: "none",
        '.Mui-selected': {
          color: "#353c52 !important",
          borderColor: "#353c52 !important",
          fontWeight: "bold",
          borderWidth: "2px",
          backgroundColor: 'transparent !important',
        },
      },
    },
    positionCreatingRow: "bottom",
    editDisplayMode: "row",
    createDisplayMode: "row",
    enableEditing,
    enableCellActions: true,
    enablePagination,
  });



  return (
    <TableWrapper style={{ maxWidth: "100%", overflow: "auto", width: "100%" }}>
      <MaterialReactTable table={table} />
    </TableWrapper>
  );
};

export default React.memo(CustomTable);
